"use client"

import { Badge } from "@repo/ui/components/ui/badge"
import { ContactForm } from "@repo/ui/features/contact/contact-form"
import { cn } from "@repo/ui/lib/utils"
import { motion, useInView } from "framer-motion"
import { CheckCircle2, Mail, MapPin, Phone } from "lucide-react"
import { useRef, useState } from "react"

interface ContactSectionProps {
  content: Record<string, string>
}

export function ContactSection({ content }: ContactSectionProps): JSX.Element {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })
  const [submitted, setSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const contactInfo = [
    {
      icon: Phone,
      title: content["contact.info.phone.title"] || "Téléphone",
      content: content["contact.info.phone.number"] || "(+33) 06 17 09 52 40",
      subtitle: content["contact.info.phone.hours"] || "Lun–Ven 9h–18h",
      href: `tel:${(content["contact.info.phone.number"] || "+33617095240").replace(/\s/g, "")}`,
    },
    {
      icon: Mail,
      title: content["contact.info.email.title"] || "Email",
      content: content["contact.info.email.address"] || "contact@dendromap.fr",
      subtitle: content["contact.info.email.response"] || "Réponse sous 24h",
      href: `mailto:${content["contact.info.email.address"] || "contact@dendromap.fr"}`,
    },
    {
      icon: MapPin,
      title: content["contact.info.address.title"] || "Adresse",
      content: content["contact.info.address.line1"] || "20 imp. du Moulin de la Pâtissière",
      subtitle: content["contact.info.address.line2"] || "44 800 Saint-Herblain",
      href: undefined,
    },
  ]

  const itemVariants = {
    hidden: { x: 20, opacity: 0 },
    visible: (delay: number) => ({
      x: 0,
      opacity: 1,
      transition: { delay, duration: 0.5, ease: [0.22, 1, 0.36, 1] as const },
    }),
  }

  return (
    <section id="contact" className="py-16 md:py-24 bg-forest-tint" ref={ref}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">

        {/* ─── Header ─────────────────────────────────────────────────── */}
        <motion.div
          className="flex flex-col items-center gap-4 text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
        >
          <Badge variant="outline">
            {content["contact.title"] || "Contact"}
          </Badge>
          <h2 className="text-4xl font-semibold tracking-tight text-foreground md:text-5xl">
            Parlons de{" "}
            <span className="text-primary">votre projet</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl leading-relaxed">
            {content["contact.subtitle"] || "Notre équipe est là pour répondre à toutes vos questions"}
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16 items-start">

          {/* ─── Left: Form ─────────────────────────────────────────────── */}
          <motion.div
            initial={{ x: -40, opacity: 0 }}
            animate={isInView ? { x: 0, opacity: 1 } : { x: -40, opacity: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            {/* Success state */}
            {submitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex flex-col items-center gap-4 rounded-2xl border border-primary/20 bg-primary/5 px-8 py-12 text-center shadow-forest"
              >
                <div className="flex size-16 items-center justify-center rounded-full bg-primary/10">
                  <CheckCircle2 className="size-8 text-primary" aria-hidden />
                </div>
                <h3 className="text-xl font-semibold text-foreground">
                  Message envoyé !
                </h3>
                <p className="text-muted-foreground leading-relaxed max-w-sm">
                  Nous avons bien reçu votre message et vous répondrons dans les plus brefs délais.
                </p>
                <button
                  type="button"
                  onClick={() => { setSubmitted(false); setSubmitError(null) }}
                  className="text-sm text-primary hover:underline mt-2"
                >
                  Envoyer un autre message
                </button>
              </motion.div>
            ) : (
              <div className="flex flex-col gap-4">
                {submitError && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="rounded-xl border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive"
                    role="alert"
                  >
                    {submitError}
                  </motion.div>
                )}
                <ContactForm
                  onSuccess={() => {
                    setSubmitError(null)
                    setSubmitted(true)
                  }}
                  onError={(error) => {
                    setSubmitError(`Erreur lors de l'envoi : ${error}`)
                  }}
                />
              </div>
            )}
          </motion.div>

          {/* ─── Right: Contact info ─────────────────────────────────────── */}
          <motion.div
            initial={{ x: 40, opacity: 0 }}
            animate={isInView ? { x: 0, opacity: 1 } : { x: 40, opacity: 0 }}
            transition={{ duration: 0.7, delay: 0.35 }}
            className="flex flex-col gap-6"
          >
            <h3 className="text-xl font-semibold text-foreground">
              {content["contact.info.title"] || "Informations de contact"}
            </h3>

            <div className="flex flex-col gap-4">
              {contactInfo.map((item, index) => (
                <motion.div
                  key={item.title}
                  custom={0.5 + index * 0.1}
                  variants={itemVariants}
                  initial="hidden"
                  animate={isInView ? "visible" : "hidden"}
                  whileHover={{ x: 4 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  className="group flex items-start gap-4 rounded-xl bg-card border border-border p-4 shadow-sm transition-all hover:border-primary/20 hover:shadow-forest"
                >
                  <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/15 transition-colors">
                    <item.icon className="size-5 text-primary" aria-hidden />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-0.5">
                      {item.title}
                    </p>
                    {item.href ? (
                      <a
                        href={item.href}
                        className="font-medium text-foreground hover:text-primary transition-colors text-sm"
                      >
                        {item.content}
                      </a>
                    ) : (
                      <p className="font-medium text-foreground text-sm">{item.content}</p>
                    )}
                    <p className="text-xs text-muted-foreground mt-0.5">{item.subtitle}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Support card */}
            <motion.div
              custom={0.8}
              variants={itemVariants}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
              className="rounded-xl bg-primary/5 border border-primary/15 p-5"
            >
              <h4 className="font-semibold text-foreground mb-2 text-sm">
                {content["contact.support.title"] || "Support technique"}
              </h4>
              <p className="text-muted-foreground text-sm mb-4 leading-relaxed">
                {content["contact.support.description"] ||
                  "Besoin d'aide technique ? Notre équipe d'experts est disponible pour vous accompagner."}
              </p>
              <a
                href="mailto:contact@dendromap.fr"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
              >
                <Mail className="size-3.5" aria-hidden />
                contact@dendromap.fr
              </a>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
